import { lazy } from "react";
import { NonIndexRouteObject, createBrowserRouter } from "react-router-dom";
import Login from "src/common/page/login";
import Callback from "src/common/page/callback";

const Layout = lazy(() => import("src/common/page/layout"));
const Plan = lazy(() => import("src/modules/plan"));
const Coupon = lazy(() => import("src/modules/coupon"));
const AccessControl = lazy(() => import("src/modules/access-control"));
const AccountTab = lazy(() => import("src/modules/access-control/account"));
const ManageAdminsTab = lazy(
  () => import("src/modules/access-control/management")
);
const ManageACL = lazy(
  () => import("src/modules/access-control/_components/ManageACL")
);
const ManageUser = lazy(
  () => import("src/modules/access-control/users/ManageUser")
);
const UsersTab = lazy(() => import("src/modules/access-control/users"));
const RolesTab = lazy(() => import("src/modules/access-control/roles"));
const PoliciesTab = lazy(() => import("src/modules/access-control/policies"));
const PermissionsTab = lazy(
  () => import("src/modules/access-control/permissions")
);
const AuditLogsTab = lazy(() => import("src/modules/access-control/audit"));
const Members = lazy(() => import("src/modules/members"));
const MembersTab = lazy(
  () => import("src/modules/members/AllMembers/membersTab")
);
const MemberDetails = lazy(
  () => import("src/modules/members/AllMembers/memberDetails")
);
const MemberGroups = lazy(
  () => import("src/modules/members/groups/memberGroups")
);
const MemberMove = lazy(
  () => import("src/modules/members/moveMember/moveMember")
);
const GroupCreate = lazy(
  () => import("src/modules/members/groups/groupCreate")
);
const Coaches = lazy(() => import("src/modules/coaches"));
const CoachTab = lazy(() => import("src/modules/coaches/listing/coachTab"));
const CoachDetail = lazy(
  () => import("src/modules/coaches/details/CoachDetail")
);
const CouponDetails = lazy(
  () => import("src/modules/coupon/details/couponDetails")
);
const CouponListing = lazy(
  () => import("src/modules/coupon/listing/couponListing")
);
const CouponMembersAll = lazy(
  () => import("src/modules/coupon/details/memberAll")
);
const CouponMemberGroup = lazy(
  () => import("src/modules/coupon/details/memberGroups")
);
const CouponOnboarding = lazy(
  () => import('src/modules/coupon/details/onboarding')
)
const Programme = lazy(() => import("src/modules/programmes"));
const ProgrammeDetail = lazy(
  () => import("src/modules/programmes/ProgrammeDetail")
);
const ProgrammeList = lazy(
  () => import("src/modules/programmes/ProgrammeList")
);
const ProgrammeAddMember = lazy(
  () => import("src/modules/programmes/AddMembers")
);
const ProgrammeChangeStatus = lazy(
  () => import("src/modules/programmes/ChangeStatus")
);

export interface RouteObjectExt extends NonIndexRouteObject {
  children?: RouteObjectExt[];
  icon?: string;
  acl?: string[];
  flag?: boolean; // Render or not, currently only handle for sidemenu
  to?: string; // Temp solution for alternate redirect
  pageTitle?: string; // If not inserted, document title will defaulted to last index of "/" in URL, for GA
}

export const routes: RouteObjectExt[] = [
  { path: 'callback', element: <Callback /> },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: 'plans',
        element: <Plan />,
        icon: 'plans',
        acl: ['plan.read'],
        flag: false,
      },
      {
        path: 'programmes',
        element: <Programme />,
        icon: 'clipboard-notes',
        acl: ['member.read'],
        children: [
          {
            path: '',
            element: <ProgrammeList />,
            acl: ['member.read'],
          },
          {
            path: 'detail',
            element: <ProgrammeDetail />,
            acl: ['member.read', 'member.edit'],
            pageTitle: 'Programme Detail',
            children: [
              {
                path: 'add-member',
                element: <ProgrammeAddMember />,
                acl: ['member.read', 'member.edit'],
                pageTitle: 'Programme Add Member',
              },
              {
                path: 'change-status',
                element: <ProgrammeChangeStatus />,
                acl: ['member.read', 'member.edit'],
                pageTitle: 'Change Programme Status',
              },
            ],
          },
        ],
      },
      {
        path: 'coupons',
        element: <Coupon />,
        icon: 'coupon',
        to: 'coupons',
        acl: ['coupon.read'],
        children: [
          {
            path: '',
            element: <CouponListing mode={'view'} />,
            acl: ['coupon.read'],
          },
          {
            path: '',
            element: <CouponDetails mode={'update'} />,
            acl: ['coupon.read', 'coupon.edit'],
            children: [
              {
                path: 'coupon-member-all',
                element: <CouponMembersAll />,
                acl: ['coupon.read', 'member.read'],
              },
              {
                path: 'coupon-member-groups',
                element: <CouponMemberGroup />,
                acl: ['coupon.read', 'group.read'],
              },
              {
                path: 'onboarding',
                element: <CouponOnboarding />,
                acl: ['coupon.read', 'group.read'],
              },
            ],
          },
        ],
      },
      {
        path: 'members',
        element: <Members />,
        icon: 'users',
        acl: ['member.read'],
        to: 'members/member-all',
        children: [
          {
            path: 'member-all',
            element: <MembersTab />,
            acl: ['member.read'],
          },
          {
            path: 'member-details',
            element: <MemberDetails />,
            acl: ['member.read'],
          },
          {
            path: 'member-groups',
            element: <MemberGroups />,
            acl: ['group.read'],
          },
          {
            path: 'move-member-code',
            element: <MemberMove />,
            acl: ['member.read', 'member.edit'],
          },
          {
            path: 'group-view',
            element: <GroupCreate mode="view" />,
            acl: ['group.read'],
          },
          {
            path: 'group-create',
            element: <GroupCreate mode="create" />,
            acl: ['group.create'],
          },
          {
            path: 'group-update',
            element: <GroupCreate mode="edit" />,
            acl: ['group.edit'],
          },
        ],
      },
      {
        path: 'coaches',
        element: <Coaches />,
        icon: 'coaches',
        acl: ['coach.read'],
        to: 'coaches/coach-list',
        children: [
          {
            path: 'coach-list',
            element: <CoachTab />,
            acl: ['coach.read'],
          },
          {
            path: 'coach-create',
            element: <CoachDetail mode="create" />,
            acl: ['coach.create'],
          },
          {
            path: 'coach-details',
            element: <CoachDetail mode="view" />,
            acl: ['coach.read'],
          },
          {
            path: 'coach-update',
            element: <CoachDetail mode="update" />,
            acl: ['coach.edit'],
          },
        ],
      },
      {
        path: 'settings',
        element: <AccessControl />,
        to: 'settings/account',
        icon: '',
        children: [
          {
            path: 'account',
            element: <AccountTab />,
          },
          {
            path: 'management',
            element: <ManageAdminsTab />,
            children: [
              {
                path: 'users',
                element: <UsersTab />,
                acl: ['acl.user.read'],
                pageTitle: 'Users',
              },
              {
                path: 'roles',
                element: <RolesTab />,
                acl: ['acl.role.read'],
                pageTitle: 'Roles',
              },
            ],
          },
          {
            path: 'management/users/edit',
            element: <ManageUser />,
            acl: ['acl.user.read', 'acl.user.edit'],
            pageTitle: 'Edit User',
          },
          {
            path: 'management/roles/edit',
            element: <ManageACL mode="edit" type="role" />,
            acl: ['acl.role.read', 'acl.role.edit'],
            pageTitle: 'Edit Role',
          },
          {
            path: 'management/roles/create',
            element: <ManageACL mode="create" type="role" />,
            acl: ['acl.role.create'],
            pageTitle: 'Create Role',
          },
          {
            path: 'policies',
            element: <PoliciesTab />,
            acl: ['acl.policy.read'],
          },
          {
            path: 'policies/edit',
            element: <ManageACL mode="edit" type="policy" />,
            acl: ['acl.policy.read', 'acl.policy.edit'],
            pageTitle: 'Edit Policy',
          },
          {
            path: 'policies/create',
            element: <ManageACL mode="create" type="policy" />,
            acl: ['acl.policy.create'],
            pageTitle: 'Create Policy',
          },
          {
            path: 'permissions',
            element: <PermissionsTab />,
            acl: ['acl.permission.read'],
          },
          {
            path: 'audit-logs',
            element: <AuditLogsTab />,
            acl: ['audit.read'],
          },
        ],
      },
    ],
  },
  {
    path: 'unauthorised',
    element: <Login noPermission={true} />,
  },
  {
    path: '*',
    element: <Login pageNotFound={true} />,
  },
]

export default createBrowserRouter(routes);
